import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  List,
  ListItemButton,
  Typography,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import type { IContact, NewContactForm } from "../../types/venues.types";
import CustomTextField from "../../../../components/ui/Textfield";
import PhoneNumberField from "../../../../components/ui/PhoneNumberField";
import apiService from "../../../../service/Api";

const DEBOUNCE_MS = 400;
const MIN_CHARS = 2;

interface AddContactModalProps {
  open: boolean;
  onClose: () => void;
  savingContact: boolean;
  newContactForm: NewContactForm;
  setNewContactForm: (form: NewContactForm) => void;
  onAddExistingContact: (contact: IContact) => Promise<void>;
  onAddNewContact: () => void;
}

const EMPTY_FORM: NewContactForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  role: "",
};

export const AddOtherContactsModal: React.FC<AddContactModalProps> = ({
  open,
  onClose,
  newContactForm,
  setNewContactForm,
  onAddExistingContact,
  onAddNewContact,
  savingContact,
}) => {
  const [checking, setChecking] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<IContact[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchAttempted, setSearchAttempted] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Reset everything when modal opens/closes
  useEffect(() => {
    if (!open) {
      setSearchTerm("");
      setSearchResults([]);
      setSearchAttempted(false);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (searchTerm.trim().length < MIN_CHARS) {
      setSearchResults([]);
      setSearchAttempted(false);
      return;
    }

    timerRef.current = setTimeout(async () => {
      setIsSearching(true);
      setSearchAttempted(true);
      try {
        const res = await apiService.get<{ results: IContact[] }>(
          `/api/contacts/contact/?contact_type=individual&search=${encodeURIComponent(searchTerm.trim())}`,
        );
        setSearchResults(res.data.results || []);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [searchTerm]);

  const handleClose = () => {
    setNewContactForm(EMPTY_FORM);
    setSearchTerm("");
    setSearchResults([]);
    setSearchAttempted(false);
    onClose();
  };

  const handleSelectExisting = async (contact: IContact) => {
    await onAddExistingContact(contact);
    handleClose();
  };

  const handleSaveNew = async () => {
    const email = newContactForm.email.trim().toLowerCase();
    const firstName = newContactForm.first_name.trim().toLowerCase();
    const lastName = newContactForm.last_name.trim().toLowerCase();

    setChecking(true);
    try {
      // Check by email
      if (email) {
        const emailRes = await apiService.get<{ results: IContact[] }>(
          `/api/contacts/contact/?search=${encodeURIComponent(newContactForm.email.trim())}`,
        );
        const emailMatch = emailRes.data.results?.find(
          (c) => c.email?.toLowerCase() === email,
        );
        if (emailMatch) {
          await onAddExistingContact(emailMatch);
          handleClose();
          return;
        }
      }

      // Check by full name
      const nameRes = await apiService.get<{ results: IContact[] }>(
        `/api/contacts/contact/?search=${encodeURIComponent(`${newContactForm.first_name.trim()} ${newContactForm.last_name.trim()}`)}`,
      );
      const nameMatch = nameRes.data.results?.find(
        (c) =>
          c.first_name?.toLowerCase() === firstName &&
          c.last_name?.toLowerCase() === lastName,
      );
      if (nameMatch) {
        await onAddExistingContact(nameMatch);
        handleClose();
        return;
      }
    } catch {
      // fall through to create
    } finally {
      setChecking(false);
    }

    onAddNewContact();
  };

  const isBusy = savingContact || checking;
  const isValid =
    newContactForm.first_name.trim() &&
    newContactForm.last_name.trim() &&
    newContactForm.email.trim();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Contact</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {/* ── Search existing ── */}
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
          Search Existing Contacts
        </Typography>
        <CustomTextField
          fullWidth
          placeholder="Search by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          disabled={isBusy}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {isSearching ? (
                  <CircularProgress size={16} />
                ) : (
                  <SearchIcon fontSize="small" />
                )}
              </InputAdornment>
            ),
          }}
        />

        {/* Search results */}
        {searchAttempted && !isSearching && (
          <Box sx={{ mt: 1, mb: 1 }}>
            {searchResults.length > 0 ? (
              <List dense sx={{ maxHeight: 160, overflow: "auto", p: 0 }}>
                {searchResults.map((contact) => (
                  <ListItemButton
                    key={contact.id}
                    onClick={() => handleSelectExisting(contact)}
                    disabled={isBusy}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      mb: 0.5,
                    }}
                  >
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {contact.first_name} {contact.last_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {contact.email}
                        {contact.role ? ` · ${contact.role}` : ""}
                      </Typography>
                    </Box>
                  </ListItemButton>
                ))}
              </List>
            ) : (
              <Typography variant="caption" color="text.secondary">
                No contacts found for &quot;{searchTerm}&quot;
              </Typography>
            )}
          </Box>
        )}

        <Divider sx={{ my: 2 }}>
          <Typography variant="caption" color="text.secondary">
            OR CREATE NEW
          </Typography>
        </Divider>

        {/* ── Create new form ── */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <CustomTextField
            fullWidth
            label="First Name"
            value={newContactForm.first_name}
            onChange={(e) =>
              setNewContactForm({
                ...newContactForm,
                first_name: e.target.value,
              })
            }
            disabled={isBusy}
          />
          <CustomTextField
            fullWidth
            label="Last Name"
            value={newContactForm.last_name}
            onChange={(e) =>
              setNewContactForm({
                ...newContactForm,
                last_name: e.target.value,
              })
            }
            disabled={isBusy}
          />
          <CustomTextField
            fullWidth
            label="Email"
            type="email"
            value={newContactForm.email}
            onChange={(e) =>
              setNewContactForm({ ...newContactForm, email: e.target.value })
            }
            disabled={isBusy}
          />
          <PhoneNumberField
            fullWidth
            defaultCountry="ZW"
            label="Phone Number"
            disabled={isBusy}
            value={newContactForm.phone ?? ""}
            onChange={(newPhoneNumber) =>
              setNewContactForm({ ...newContactForm, phone: newPhoneNumber })
            }
          />
          <CustomTextField
            fullWidth
            label="Role"
            value={newContactForm.role}
            onChange={(e) =>
              setNewContactForm({ ...newContactForm, role: e.target.value })
            }
            disabled={isBusy}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isBusy}>
          Cancel
        </Button>
        <Button
          onClick={handleSaveNew}
          variant="contained"
          disabled={!isValid || isBusy}
        >
          {isBusy ? <CircularProgress size={20} /> : "Add Contact"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddOtherContactsModal;
